import { useState } from "react";
import StressProfileEditor from "components/experiment/StressProfileEditor";
import WaterEventsEditor from "components/experiment/WaterEventsEditor";
import { normalizeWaterEvents } from "utils/waterEvents";

export default function StressProfileManager({
  profiles,
  steps,
  onStepsChange,
  selectedProfileId,
  onSelectProfile,
  onCreateProfile,
  onArchiveProfile,
  waterEvents = [],
  onWaterEventsChange = () => {},
  pumpConfig,
  waterEventRowErrors,
  startLevelCm,
}) {
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const selectedProfile = profiles.find((p) => p.id === selectedProfileId) || null;

  const handleSelect = (event) => {
    const id = event.target.value ? Number(event.target.value) : "";
    onSelectProfile(id || "");
    if (id) {
      const profile = profiles.find((p) => p.id === id);
      if (profile) {
        onStepsChange(profile.steps);
        // Profiles saved before water events existed have none.
        onWaterEventsChange(profile.water_events ?? []);
      }
    }
  };

  // The saved profile's events as the editor/payload see them (numbers, fixed key order) - the
  // server's JSON may order keys differently, so never compare the raw objects.
  const savedWaterEvents = (profile) => JSON.stringify(normalizeWaterEvents(profile?.water_events ?? []));

  // Real bug found 2026-09-25: greenhouse_start_run deliberately ignores
  // any inline `profile` steps whenever `profile_id` is also sent (see its
  // docstring - profile_id always wins, so a client can never claim "ran
  // profile 7" while smuggling different steps). That's the right call
  // server-side, but this editor let the operator edit a selected saved
  // profile's steps in place with no client-side signal that their edits
  // would be silently discarded and the ORIGINAL saved steps run instead -
  // exactly what got reported. Fix: the moment an edit actually diverges
  // from the selected profile's own steps, drop the selection back to
  // "Build ad hoc (not saved)" so profileId is no longer sent and the
  // edited steps are what the backend actually validates and runs.
  const handleStepsChange = (newSteps) => {
    onStepsChange(newSteps);
    if (selectedProfile && JSON.stringify(newSteps) !== JSON.stringify(selectedProfile.steps)) {
      onSelectProfile("");
    }
  };

  // Same rule for the water events: the backend uses the saved profile's own events whenever
  // profile_id is sent, so an edit that differs from them must drop the selection or it would be
  // silently ignored.
  const handleWaterEventsChange = (newEvents) => {
    onWaterEventsChange(newEvents);
    if (selectedProfile && JSON.stringify(normalizeWaterEvents(newEvents)) !== savedWaterEvents(selectedProfile)) {
      onSelectProfile("");
    }
  };

  const handleSaveAsNew = async () => {
    if (!saveName.trim()) {
      setSaveMessage("Name the profile before saving.");
      return;
    }
    setSaving(true);
    setSaveMessage("");
    try {
      await onCreateProfile({
        name: saveName.trim(),
        steps,
        waterEvents: normalizeWaterEvents(waterEvents),
      });
      setSaveMessage(`Saved "${saveName.trim()}".`);
      setSaveName("");
    } catch (error) {
      setSaveMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedProfile) return;
    setSaving(true);
    try {
      await onArchiveProfile(selectedProfile.id);
      onSelectProfile("");
    } catch (error) {
      setSaveMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
        <label className="block flex-1">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Saved profile
          </span>
          <select
            aria-label="Saved profile"
            value={selectedProfileId || ""}
            onChange={handleSelect}
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            <option value="">Build ad hoc (not saved)</option>
            {profiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name} — {profile.steps.length} step{profile.steps.length === 1 ? "" : "s"}
                {(profile.water_events || []).length > 0
                  ? `, ${profile.water_events.length} water event${profile.water_events.length === 1 ? "" : "s"}`
                  : ""}
              </option>
            ))}
          </select>
        </label>
        {selectedProfile && (
          <button
            type="button"
            onClick={handleArchive}
            disabled={saving}
            className="rounded-xl border border-red-200 bg-white px-3 py-2 text-xs font-bold text-red-700 disabled:opacity-50"
          >
            Archive
          </button>
        )}
      </div>

      <StressProfileEditor steps={steps} onChange={handleStepsChange} />
      <WaterEventsEditor
        events={waterEvents}
        onChange={handleWaterEventsChange}
        config={pumpConfig || undefined}
        rowErrors={waterEventRowErrors}
        startLevelCm={startLevelCm}
      />
      {selectedProfile && (
        <p className="text-xs text-slate-500">
          Editing these steps or water events runs a one-off, ad-hoc version for this run only — it switches back to
          "Build ad hoc" above and won't change the saved "{selectedProfile.name}" profile.
        </p>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-2">
        <label className="block flex-1 text-xs font-semibold text-slate-600">
          Save current steps and water events as new profile
          <input
            value={saveName}
            onChange={(event) => setSaveName(event.target.value)}
            placeholder="e.g. tropical-afternoon"
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={handleSaveAsNew}
          disabled={saving}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save as new profile"}
        </button>
      </div>
      {saveMessage && <p role="status" className="text-xs text-slate-500">{saveMessage}</p>}
    </div>
  );
}
